import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import ErrorHandler from '../../lib/error-handler';
import { waitForClerk } from '../types/clerk-shim';

declare var Penmate;

@Injectable({ providedIn: 'root' })
export class ClerkAuthService {
  private clerkInstance: any;
  private userSubject = new BehaviorSubject<any>(null);
  private sessionSubject = new BehaviorSubject<any>(null);
  
  public user$ = this.userSubject.asObservable();
  public session$ = this.sessionSubject.asObservable();
  
  constructor(private storage: Storage) {
    this.initializeClerk();
  }
  
  private async initializeClerk() {
    try {
      // Wait for Clerk to be available and loaded from CDN
      this.clerkInstance = await waitForClerk();
      
      // Set up listeners for user and session changes
      this.clerkInstance.addListener((emission) => {
        if (emission.user) {
          this.userSubject.next(emission.user);
        }
        if (emission.session) {
          this.sessionSubject.next(emission.session);
        }
      });
      
      // Check if user is already signed in
      const user = this.clerkInstance.user;
      const session = this.clerkInstance.session;
      
      if (user) {
        this.userSubject.next(user);
      }
      if (session) {
        this.sessionSubject.next(session);
      }
    } catch (error) {
      ErrorHandler.captureException(error, 'Clerk initialization error');
      console.error('Failed to initialize Clerk:', error);
    }
  }
  
  getClerkInstance(): any {
    return this.clerkInstance;
  }
  
  async getCurrentSession(): Promise<any> {
    if (!this.clerkInstance) {
      await this.initializeClerk();
    }
    return this.clerkInstance ? this.clerkInstance.session : null;
  }
  
  async getCurrentUser(): Promise<any> {
    if (!this.clerkInstance) {
      await this.initializeClerk();
    }
    return this.clerkInstance ? this.clerkInstance.user : null;
  }
  
  async signOut(): Promise<void> {
    if (this.clerkInstance) {
      await this.clerkInstance.signOut({ redirectUrl: '/login' });
      await this.storage.remove('clerk_sign_in_token');
      this.userSubject.next(null);
      this.sessionSubject.next(null);
    }
  }
  
  async signInWithToken(signInToken: string): Promise<boolean> {
    try {
      if (!this.clerkInstance) {
        await this.initializeClerk();
      }
      
      const result = await this.clerkInstance.signIn.create({
        strategy: 'ticket',
        ticket: signInToken
      });
      
      return result.status === 'complete';
    } catch (error) {
      ErrorHandler.captureException(error, 'Clerk sign in with token error');
      return false;
    }
  }
  
  isAuthenticated(): Observable<boolean> {
    return this.session$.pipe(
      map(session => !!session && session.status === 'active')
    );
  }
  
  waitForAuthentication(): Observable<any> {
    return this.session$.pipe(
      filter(session => !!session && session.status === 'active')
    );
  }
}